import { Injectable, inject } from '@angular/core';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { orderBy } from 'lodash-es';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap, withLatestFrom } from 'rxjs/operators';

import { SIMILAR_PARAGRAPH_DEAL_COUNT_LIMIT } from '../../features/deal/constants/limits';
import { SimilarParagraphService } from '../../features/similar-paragraph/services/similar-paragraph.service';
import { DealFilterSelectors } from '../deal-filter/deal-filter.selectors';
import { RootState } from '../root-state.model';
import { SimilarParagraphActions } from './similar-paragraph.actions';
import { TransactionSelectors } from '../transaction/transaction.selectors';

@Injectable()
export class SimilarParagraphsEffects {
  private service = inject(SimilarParagraphService);
  private actions$ = inject(Actions);
  private readonly store = inject<Store<RootState>>(Store);

  search$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(SimilarParagraphActions.SearchSimilarParagraphs),
      withLatestFrom(
        this.store,
        this.store.select(DealFilterSelectors.filteredTransactions),
        this.store.select(TransactionSelectors.listLookup)
      ),
      switchMap(([action, store, transactions, listLookup]) => {
        const searchText = action.searchText || store.similarParagraph.originalParagraph;
        let transactionList = orderBy(
          transactions || [],
          (t) => t.transactionDisplayClosingDate || false,
          'desc'
        ).filter((t) => t.transactionId !== store.deal.transaction.transactionId);

        transactionList = action.scanWithoutLimit
          ? transactionList
          : transactionList.slice(0, SIMILAR_PARAGRAPH_DEAL_COUNT_LIMIT);

        return this.service
          .getSimilars(
            searchText,
            transactionList.map((t) => t.transactionId)
          )
          .pipe(
            map((list) => {
              return this.service.createViewModels(list, listLookup);
            }),
            switchMap((viewModels) => this.service.makeDiff(viewModels, searchText)),
            map((list) => SimilarParagraphActions.SearchSimilarParagraphsSuccess({ list })),
            catchError((error: Error) =>
              of(SimilarParagraphActions.SearchSimilarParagraphsFail({ error: error.message }))
            )
          );
      })
    )
  );
}
